import {ImageExportType} from '@wandb/common/util/panelExport';
import React, {FC, forwardRef, useContext, useState} from 'react';
import {Divider, Menu, Popup} from 'semantic-ui-react';

import {PanelBankUpdaterContext} from '../../state/panelbank/context';
import {RunQueryContext} from '../../state/runs/context';
import * as ViewHooks from '../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../state/views/panelBankSectionConfig/actions';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {getPanelSpec, isExportable} from '../../util/panels';
import {Button} from '../Button';
import {Icon} from '../Icon';
import {DefaultMovePanelOption, MovePanelSubmenu} from './MovePanelSubmenu';
import {
  StyledButtonWrapper,
  StyledMenu,
  StyledMenuItem,
} from './PanelActions.style';
import {PanelContext} from './PanelContextProvider';
import {PanelExportMenu} from './PanelExportMenu';

const POPUP_WIDTH = 154;
const PANEL_MOVER_LEFT_OFFSET = -82;

export enum ExportType {
  API,
  CSV,
  Image,
}

enum Submenu {
  MOVE_PANEL,
  EXPORT_PANEL,
}

interface PanelActionsOverflowProps {
  defaultMoveToSectionRef?: PanelBankSectionConfigTypes.Ref;
  panelSpec: ReturnType<typeof getPanelSpec>;
  setExportType: (exportType: ExportType) => void;
  setImageExportType: (imageType: ImageExportType) => void;
}

export const PanelActionsMenu: FC<PanelActionsOverflowProps> = forwardRef<
  HTMLElement,
  PanelActionsOverflowProps
>(
  (
    {defaultMoveToSectionRef, panelSpec, setExportType, setImageExportType},
    ref: React.Ref<HTMLElement>
  ) => {
    const [activeSubmenu, setActiveSubmenu] = useState<Submenu>();
    const [isOpen, setIsOpen] = useState(false);

    const runsContext = useContext(RunQueryContext);
    const {setExportingPanelRefID} = useContext(PanelBankUpdaterContext);
    const {panelBankSectionConfigRef, panelBankConfigRef, panelRef} =
      useContext(PanelContext);

    const duplicatePanel = ViewHooks.useViewAction(
      panelBankSectionConfigRef,
      PanelBankSectionConfigActions.duplicatePanel
    );

    const deletePanel = ViewHooks.useViewAction(
      panelBankSectionConfigRef,
      PanelBankSectionConfigActions.deletePanel
    );
    const onRemovePanel = () => deletePanel(panelRef, panelBankConfigRef);

    const handleOpen = () => setIsOpen(true);

    const handleClose = () => {
      setActiveSubmenu(undefined);
      setIsOpen(false);
    };

    return (
      <Popup
        content="More actions"
        inverted
        size="mini"
        position="top center"
        trigger={
          <div>
            <Popup
              className="popup-menu"
              basic
              position="bottom right"
              on="click"
              onOpen={handleOpen}
              open={isOpen}
              onClose={handleClose}
              style={{
                width: POPUP_WIDTH,
                top: -8,
                left:
                  activeSubmenu === Submenu.MOVE_PANEL
                    ? PANEL_MOVER_LEFT_OFFSET
                    : undefined,
              }}
              // offset="0, -16px"
              // offset={'-320px, -16px'}
              trigger={
                <StyledButtonWrapper
                  className="panel-action-icon-overflow"
                  onClick={(e: React.SyntheticEvent) => e.stopPropagation()}>
                  <Button
                    style={{color: 'inherit'}}
                    icon="overflow-horizontal"
                    variant="ghost"
                    size="small"
                  />
                </StyledButtonWrapper>
              }
              content={
                <>
                  {activeSubmenu == null && (
                    <StyledMenu
                      vertical
                      borderless
                      style={{width: POPUP_WIDTH}}>
                      <Menu.Item
                        className="panel-action-add-to-report panel-action"
                        onClick={() => {
                          window.analytics?.track('Add to Report Clicked', {
                            location: 'panel menu',
                          });
                          setExportingPanelRefID(panelRef.id);
                          handleClose();
                        }}>
                        <StyledMenuItem>
                          <Icon name="report" />
                          Add to report
                        </StyledMenuItem>
                      </Menu.Item>
                      <Menu.Item
                        className="panel-action-duplicate panel-action panel-action-icon-copy"
                        onClick={() => {
                          duplicatePanel(panelRef);
                          handleClose();
                        }}>
                        <StyledMenuItem>
                          <Icon name="copy" />
                          Duplicate
                        </StyledMenuItem>
                      </Menu.Item>
                      {runsContext.report == null && (
                        <>
                          {defaultMoveToSectionRef &&
                            panelBankConfigRef &&
                            panelBankSectionConfigRef !==
                              defaultMoveToSectionRef && (
                              <DefaultMovePanelOption
                                panelBankConfigRef={panelBankConfigRef}
                                toSectionRef={defaultMoveToSectionRef}
                              />
                            )}
                          <Menu.Item
                            className="panel-action panel-action-icon-move"
                            onClick={() =>
                              setActiveSubmenu(Submenu.MOVE_PANEL)
                            }>
                            <StyledMenuItem>
                              <Icon name="folder-project-move" />
                              Move
                            </StyledMenuItem>
                          </Menu.Item>
                        </>
                      )}
                      {isExportable(panelSpec) && (
                        <PanelExportMenu
                          panelSpec={panelSpec}
                          handleClose={handleClose}
                          setExportType={setExportType}
                          setImageExportType={setImageExportType}
                        />
                      )}
                      <Divider fitted />
                      <Menu.Item
                        className="panel-action-delete  panel-action panel-action-icon-delete"
                        onClick={() => {
                          onRemovePanel();
                          handleClose();
                        }}>
                        <StyledMenuItem>
                          <Icon name="delete" />
                          Delete
                        </StyledMenuItem>
                      </Menu.Item>
                    </StyledMenu>
                  )}
                  {activeSubmenu === Submenu.MOVE_PANEL &&
                    panelBankConfigRef &&
                    panelBankSectionConfigRef && (
                      // The "Move panel to {default section}" menu option
                      <MovePanelSubmenu
                        panelBankConfigRef={panelBankConfigRef}
                        panelBankSectionConfigRef={panelBankSectionConfigRef}
                      />
                    )}
                </>
              }
            />
          </div>
        }
      />
    );
  }
);
